import { BaseText, Headline3, Title } from 'aimo-ui';
import useCreateFeedback from 'hooks/useCreateFeedback';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { isWeb } from 'utils/deviceUtils';
import { rateApp } from 'utils/rateAppUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import FeedbackIconButton from './FeedbackIconButton';
import FeedbackInputSection from './FeedbackInputSection';

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: 8,
    width: '80%',
    paddingHorizontal: 40,
    padding: 5,
    margin: 'auto',
    maxWidth: 600,
  },
});

export type FeedbackType = 'NEGATIVE' | 'NEUTRAL' | 'POSITIVE';

export type FeedbackConfig = {
  id: FeedbackType;
  icon: string;
  value: number;
};

const feedbackConfigMap: Record<string, FeedbackConfig> = {
  NEGATIVE: { id: 'NEGATIVE', icon: 'Review-Negative', value: -1 },
  NEUTRAL: { id: 'NEUTRAL', icon: 'Review-Neutral', value: 0 },
  POSITIVE: { id: 'POSITIVE', icon: 'Review-Positive', value: 1 },
};

const Feedback: React.FC<{ sessionId: string; intent: string }> = ({
  sessionId,
  intent,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const { createFeedback } = useCreateFeedback();
  const submitFeedback = async (selectedFeedbackId: string) => {
    const feedbackData = feedbackConfigMap[selectedFeedbackId];
    if (!feedbackData) {
      console.error('No matching feedback config found!');
      return;
    }

    const feedback = {
      feedbackComponents: {
        rating: {
          description: feedbackMessage,
          score: feedbackData.value.toString(),
        },
      },
      intent: intent,
      metaData: {
        channel: 'AIMO_APP',
        sentiment: feedbackData.id,
        tags: ['STP', 'NON_LOGGED_IN'],
      },
      transactionId: sessionId,
    };
    await createFeedback(feedback, setIsFeedbackSubmitted);
  };

  if (isFeedbackSubmitted) {
    return (
      <>
        <StyledWrapper mt={20}>
          <Title center>{'❤️'}</Title>
        </StyledWrapper>
        <StyledWrapper center maxWidth={250} mb={0}>
          <BaseText center {...testIdentifiers(`feedback-thanks`)}>
            {t('feedback.thanks')}
          </BaseText>
        </StyledWrapper>
      </>
    );
  }

  return (
    <>
      <StyledWrapper mb={20} mt={20}>
        <Headline3 center>{t('feedback.title')}</Headline3>
      </StyledWrapper>
      <View style={styles.buttonContainer}>
        {Object.keys(feedbackConfigMap).map((key) => (
          <FeedbackIconButton
            key={feedbackConfigMap[key].id}
            config={feedbackConfigMap[key]}
            selected={selected}
            onPress={() => {
              setSelected(feedbackConfigMap[key].id);
              if (isWeb && feedbackConfigMap[key].id === 'POSITIVE') {
                submitFeedback(feedbackConfigMap[key].id);
              }
            }}
          />
        ))}
      </View>
      {(selected === 'NEGATIVE' || selected === 'NEUTRAL') && (
        <FeedbackInputSection
          title={t('feedback.negativeTitle')}
          description={t('feedback.negativeDescription')}
          buttonText={t('feedback.button')}
          onButtonPress={() => submitFeedback(selected)}
          feedbackMessage={feedbackMessage}
          setFeedbackMessage={setFeedbackMessage}
          {...testIdentifiers(`feedback-submit-button`)}
        />
      )}
      {selected === 'POSITIVE' && !isWeb && (
        <FeedbackInputSection
          title={t('feedback.positiveTitle')}
          description={t('feedback.positiveDescription')}
          buttonText={t('feedback.rateButton')}
          onButtonPress={async () => {
            if (isWeb) {
              return submitFeedback(selected);
            }
            const done = await rateApp();
            submitFeedback(selected);
            if (done) {
              setIsFeedbackSubmitted(true);
            }
          }}
        />
      )}
    </>
  );
};

export default Feedback;
